/**
 * Hooks for trust and governance screens: Safety Centre history, agents, approvals, settings
 * (environments, members, secrets, webhooks, danger zone), compliance (audit log) and sign-in.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, idem, unwrap } from "../client";
import { qk } from "../keys";
import { useMe } from "./workspace";
import type { Schemas } from "../types";

type EnvironmentUpdate = Schemas["EnvironmentUpdate"];
type WebhookCreate = Schemas["WebhookCreate"];
type WebhookUpdate = Schemas["WebhookUpdate"];
type MemberInvite = Schemas["MemberInvite"];
type ProjectUpdate = Schemas["ProjectUpdate"];
type AgentTokenUpdate = Schemas["AgentTokenUpdate"];
type SecretRotateRequest = Schemas["SecretRotateRequest"];
type StepUpPurpose = Schemas["StepUpBeginRequest"]["purpose"];

/* Audit log -------------------------------------------------------------- */

/** Audit events, optionally filtered by action (e.g. "safety.grade_changed"). Newest first. */
export function useAuditLog(workspace: string, filter: { action?: string; since?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: qk.auditEvents(workspace, filter),
    queryFn: () =>
      unwrap(
        api.GET("/v1/workspaces/{workspace}/audit-events", {
          params: { path: { workspace }, query: { limit: filter.limit ?? 200, action: filter.action, since: filter.since } },
        }),
      ),
    enabled: !!workspace,
  });
}

/* Agents ----------------------------------------------------------------- */

export function useUpdateAgentToken(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tokenId, body }: { tokenId: string; body: AgentTokenUpdate }) =>
      unwrap(
        api.PATCH("/v1/workspaces/{workspace}/agent-tokens/{token_id}", {
          params: { path: { workspace, token_id: tokenId }, header: idem() },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.agentTokens(workspace) }),
  });
}

/**
 * The platform AI kill switch (docs/13 §7). Reading it needs `admin:platform`; everyone else gets 403,
 * which screens treat as "not engaged as far as we know" because agent calls would fail loudly otherwise.
 */
export function useKillSwitch() {
  const me = useMe();
  return useQuery({
    queryKey: ["kill-switch"],
    queryFn: () => unwrap(api.GET("/v1/admin/ai-ops/kill-switch")),
    // Only platform admins may read it; skip the request (and its 403) for everyone else.
    enabled: me.data?.user?.is_platform_admin === true,
    retry: false,
    staleTime: 60_000,
  });
}

/* Step-up (passkey) ------------------------------------------------------ */

export function useBeginStepUp() {
  return useMutation({
    mutationFn: (body: { purpose: StepUpPurpose; subject_id?: string }) =>
      unwrap(api.POST("/v1/auth/step-up/begin", { params: { header: idem() }, body })),
  });
}

/* Environments ----------------------------------------------------------- */

export function useUpdateEnvironment(project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ environment, body }: { environment: string; body: EnvironmentUpdate }) =>
      unwrap(
        api.PATCH("/v1/projects/{project}/environments/{environment}", {
          params: { path: { project, environment }, header: idem() },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.environments(project) }),
  });
}

export function useDeleteEnvironment(project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (environment: string) =>
      unwrap(
        api.DELETE("/v1/projects/{project}/environments/{environment}", {
          params: { path: { project, environment }, query: { confirm: environment }, header: idem() },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.environments(project) }),
  });
}

/* Members ---------------------------------------------------------------- */

export function useInviteMember(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MemberInvite) =>
      unwrap(api.POST("/v1/workspaces/{workspace}/members", { params: { path: { workspace }, header: idem() }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.members(workspace) }),
  });
}

export function useUpdateMember(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: MemberInvite["role"] }) =>
      unwrap(
        api.PATCH("/v1/workspaces/{workspace}/members/{member_id}", {
          params: { path: { workspace, member_id: memberId }, header: idem() },
          body: { role },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.members(workspace) }),
  });
}

export function useRemoveMember(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      unwrap(
        api.DELETE("/v1/workspaces/{workspace}/members/{member_id}", {
          params: { path: { workspace, member_id: memberId }, header: idem() },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.members(workspace) }),
  });
}

/* Variables and secrets -------------------------------------------------- */

export function useUnsetVariable(project: string, environment: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      unwrap(
        api.DELETE("/v1/projects/{project}/environments/{environment}/variables/{name}", {
          params: { path: { project, environment, name }, header: idem() },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.variables(project, environment) }),
  });
}

export function useUnsetSecret(project: string, environment: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (secret: string) =>
      unwrap(
        api.DELETE("/v1/projects/{project}/environments/{environment}/secrets/{secret}", {
          params: { path: { project, environment, secret }, header: idem() },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.secrets(project, environment) }),
  });
}

export function useRotateSecret(project: string, environment: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ secret, body }: { secret: string; body: SecretRotateRequest }) =>
      unwrap(
        api.POST("/v1/projects/{project}/environments/{environment}/secrets/{secret}/rotate", {
          params: { path: { project, environment, secret }, header: idem() },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.secrets(project, environment) }),
  });
}

/* Project (danger zone) -------------------------------------------------- */

export function useUpdateProject(project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProjectUpdate) =>
      unwrap(api.PATCH("/v1/projects/{project}", { params: { path: { project }, header: idem() }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.project(project) }),
  });
}

export function useDeleteProject(project: string, workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(api.DELETE("/v1/projects/{project}", { params: { path: { project }, query: { confirm: project }, header: idem() } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.projects(workspace) }),
  });
}

/* Webhooks --------------------------------------------------------------- */

const webhooksKey = (ws: string) => ["workspace", ws, "webhooks"] as const;
const deliveriesKey = (ws: string, id: string) => ["workspace", ws, "webhooks", id, "deliveries"] as const;

export function useWebhooks(workspace: string) {
  return useQuery({
    queryKey: webhooksKey(workspace),
    queryFn: () => unwrap(api.GET("/v1/workspaces/{workspace}/webhooks", { params: { path: { workspace } } })),
    enabled: !!workspace,
  });
}

export function useWebhookDeliveries(workspace: string, webhookId: string) {
  return useQuery({
    queryKey: deliveriesKey(workspace, webhookId),
    queryFn: () =>
      unwrap(
        api.GET("/v1/workspaces/{workspace}/webhooks/{webhook_id}/deliveries", {
          params: { path: { workspace, webhook_id: webhookId } },
        }),
      ),
    enabled: !!workspace && !!webhookId,
  });
}

/** The signing secret is in the response exactly once. */
export function useCreateWebhook(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: WebhookCreate) =>
      unwrap(api.POST("/v1/workspaces/{workspace}/webhooks", { params: { path: { workspace }, header: idem() }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: webhooksKey(workspace) }),
  });
}

export function useUpdateWebhook(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ webhookId, body }: { webhookId: string; body: WebhookUpdate }) =>
      unwrap(
        api.PATCH("/v1/workspaces/{workspace}/webhooks/{webhook_id}", {
          params: { path: { workspace, webhook_id: webhookId }, header: idem() },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: webhooksKey(workspace) }),
  });
}

export function useDeleteWebhook(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (webhookId: string) =>
      unwrap(
        api.DELETE("/v1/workspaces/{workspace}/webhooks/{webhook_id}", {
          params: { path: { workspace, webhook_id: webhookId }, header: idem() },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: webhooksKey(workspace) }),
  });
}

export function useTestWebhook(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (webhookId: string) =>
      unwrap(
        api.POST("/v1/workspaces/{workspace}/webhooks/{webhook_id}/test", {
          params: { path: { workspace, webhook_id: webhookId }, header: idem() },
        }),
      ),
    onSuccess: (_d, id) => {
      void qc.invalidateQueries({ queryKey: deliveriesKey(workspace, id) });
      void qc.invalidateQueries({ queryKey: webhooksKey(workspace) });
    },
  });
}

export function useRedeliverWebhook(workspace: string, webhookId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deliveryId: string) =>
      unwrap(
        api.POST("/v1/workspaces/{workspace}/webhooks/{webhook_id}/deliveries/{delivery_id}/redeliver", {
          params: { path: { workspace, webhook_id: webhookId, delivery_id: deliveryId }, header: idem() },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: deliveriesKey(workspace, webhookId) }),
  });
}

export function useRotateWebhookSecret(workspace: string) {
  return useMutation({
    mutationFn: (webhookId: string) =>
      unwrap(
        api.POST("/v1/workspaces/{workspace}/webhooks/{webhook_id}/rotate-secret", {
          params: { path: { workspace, webhook_id: webhookId }, header: idem() },
        }),
      ),
  });
}
